import { db } from '@/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { UtensilsCrossed, CalendarDays } from 'lucide-react';
import { startOfWeek, format, getDay, addDays } from 'date-fns';
import { useEffect, useState } from 'react';

interface MealPlannerWidgetProps {
  householdId: string | null;
  userId: string;
}

const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function MealPlannerWidget({ householdId, userId }: MealPlannerWidgetProps) {
  const navigate = useNavigate();
  const [weekStartDateStr, setWeekStartDateStr] = useState<string>('');
  const [mealPlans, setMealPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekStartDateStrVal = format(weekStart, 'yyyy-MM-dd');
    setWeekStartDateStr(weekStartDateStrVal);

    const q = householdId
      ? query(collection(db, 'households', householdId, 'mealPlans'), where('weekStartDate', '==', weekStartDateStrVal))
      : query(collection(db, 'users', userId, 'mealPlans'), where('weekStartDate', '==', weekStartDateStrVal));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const plans = snapshot.docs.map((doc) => doc.data() as any);
      setMealPlans(plans);
      setLoading(false);
    }, () => setLoading(false));

    return () => unsubscribe();
  }, [householdId, userId]);

  const getTodayDayIndex = () => {
    const today = new Date();
    const weekday = getDay(today); // 0=Sun, 1=Mon, ..., 6=Sat
    return (weekday + 6) % 7; // Convert to 0=Mon, 1=Tue, ..., 6=Sun
  };

  const todayIndex = getTodayDayIndex();
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(new Date(weekStartDateStr), i));

  if (loading) return null;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <h3 className="font-semibold text-slate-900 dark:text-white">Weekly Meal Planner</h3>
        </div>
      </div>

      <div className="p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {dayKeys.map((dayKey, index) => {
              const hasDinner = mealPlans.some(plan => plan.days?.[dayKey]?.dinner);
              const dinnerName = mealPlans
                .flatMap(plan => plan.days?.[dayKey]?.dinner)
                .filter(Boolean)[0] || '—';

              const isToday = index === todayIndex;

              return (
                <div
                  key={dayKey}
                  className={`
                    flex flex-col min-w-[120px] sm:min-w-[160px] gap-2
                    ${isToday 
                      ? 'bg-brand/10 dark:bg-brand/20 border border-brand rounded-xl p-4 flex-shrink-0' 
                      : 'bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 flex-shrink-0'
                    }
                  `}
                >
                  <span className={`font-medium text-sm ${isToday ? 'text-brand dark:text-brand-light' : 'text-slate-700 dark:text-slate-300'}`}>
                    {dayLabels[index]}
                  </span>
                  <span className="text-lg font-semibold text-slate-900 dark:text-slate-100 min-h-[1.75rem]">
                    {dinnerName}
                  </span>
                  <div className="mt-auto pt-2">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {weekDays[index].getDate()} {weekDays[index].toLocaleString('default', { month: 'short' })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {mealPlans.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="bg-slate-100 dark:bg-slate-800 rounded-full p-4 mb-4">
                <CalendarDays className="w-8 h-8 text-slate-500 dark:text-slate-400" />
              </div>
              <p className="text-slate-700 dark:text-slate-300 font-medium">No meals planned this week</p>
              <button
                onClick={() => navigate('/meal-planner')}
                className="mt-2 px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1 transition-colors"
              >
                Plan Meals <UtensilsCrossed className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="mt-4">
              <button
                onClick={() => navigate('/meal-planner')}
                className="w-full py-2 px-4 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:hover:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <UtensilsCrossed className="w-4 h-4" />
                Open Planner
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}